import mapboxgl from 'mapbox-gl';
import { LayerRegistry, WMSLayer } from '../layers2';
import { logger } from '../../utils/logger';

const log = logger('GetFeatureInfoCoordinator');

interface LayerFeatures {
  layerName: string;
  features: any[];
}

/**
 * Centralized coordinator for GetFeatureInfo requests.
 * Queries all visible WMS layers on click and combines results into one popup.
 */
export class GetFeatureInfoCoordinator {
  private map: mapboxgl.Map;
  private registry: LayerRegistry;
  private enabled: boolean = false;
  private clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private currentPopup: mapboxgl.Popup | null = null;

  constructor(map: mapboxgl.Map, registry: LayerRegistry) {
    this.map = map;
    this.registry = registry;
  }

  /**
   * Enable GetFeatureInfo mode - attach click handler and update cursor
   */
  public enable(): void {
    if (this.enabled) {
      return;
    }
    this.enabled = true;

    const canvas = this.map.getCanvas();
    const container = this.map.getContainer();

    // Use inline style with !important - CSS class approach fails due to component scoping
    canvas.style.setProperty('cursor', 'crosshair', 'important');
    container.style.setProperty('cursor', 'crosshair', 'important');

    // Attach click handler
    this.clickHandler = this.handleClick.bind(this);
    this.map.on('click', this.clickHandler);

    // Attach mousemove handler
    this.map.on('mousemove', this.handleMouseMove);

    log.debug('GetFeatureInfo coordinator enabled');
  }

  /**
   * Disable GetFeatureInfo mode - remove click handler and reset cursor
   */
  public disable(): void {
    if (!this.enabled) {
      return;
    }
    this.enabled = false;

    const canvas = this.map.getCanvas();
    const container = this.map.getContainer();

    canvas.style.removeProperty('cursor');
    container.style.removeProperty('cursor');

    if (this.clickHandler) {
      this.map.off('click', this.clickHandler);
      this.clickHandler = null;
    }

    this.map.off('mousemove', this.handleMouseMove);

    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    log.debug('GetFeatureInfo coordinator disabled');
  }

  /**
   * Check if coordinator is enabled
   */
  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Handle mouse move to restore cursor - other layers fight for cursor control
   */
  private handleMouseMove = (): void => {
    if (this.enabled) {
      const canvas = this.map.getCanvas();
      const container = this.map.getContainer();

      // Re-apply cursor in case Mapbox layer interactions changed it
      if (canvas.style.cursor !== 'crosshair') {
        canvas.style.setProperty('cursor', 'crosshair', 'important');
        container.style.setProperty('cursor', 'crosshair', 'important');
      }
    }
  };

  /**
   * Query features at a specific location — used by context menu "Get Info"
   */
  public async queryAt(lngLat: mapboxgl.LngLat, point: mapboxgl.Point): Promise<void> {
    await this.queryAndShowPopup(lngLat, point);
  }

  /**
   * Handle map click - query all visible WMS layers
   */
  private async handleClick(e: mapboxgl.MapMouseEvent): Promise<void> {
    log.debug('handleClick at', e.lngLat);
    await this.queryAndShowPopup(e.lngLat, e.point);
  }

  private async queryAndShowPopup(lngLat: mapboxgl.LngLat, point: mapboxgl.Point): Promise<void> {
    const allLayers = this.registry.getByType('wms');
    const wmsLayers = allLayers.filter(layer => layer.isEnabled()) as WMSLayer[];

    if (wmsLayers.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      wmsLayers.map(layer => this.queryLayer(layer, lngLat, point))
    );

    const layerFeatures: LayerFeatures[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.length > 0) {
        layerFeatures.push({
          layerName: wmsLayers[index].getConfig().name || `Layer ${index + 1}`,
          features: result.value
        });
      } else if (result.status === 'rejected') {
        log.warn(`GetFeatureInfo query failed for layer ${wmsLayers[index].getConfig().name}:`, result.reason);
      }
    });

    if (layerFeatures.length > 0) {
      this.showCombinedPopup(lngLat, layerFeatures);
    }
  }

  /**
   * Query a single WMS layer for features at the given location
   */
  private async queryLayer(
    layer: WMSLayer,
    lngLat: mapboxgl.LngLat,
    point: mapboxgl.Point
  ): Promise<any[]> {
    try {
      const url = layer.buildGetFeatureInfoUrl(lngLat, point);

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        return [];
      }

      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json') || contentType.includes('application/geojson')) {
        const data = await response.json();
        return data.features || [];
      } else if (contentType.includes('text/html')) {
        const html = await response.text();
        if (html.trim() && !html.includes('no features') && html.length > 100) {
          return [{ _html: html }];
        }
      } else if (contentType.includes('text/xml') || contentType.includes('application/xml')) {
        return [];
      } else {
        try {
          const data = await response.json();
          return data.features || [];
        } catch {
          return [];
        }
      }
    } catch (error) {
      return [];
    }

    return [];
  }

  /**
   * Show combined popup with features from multiple layers
   */
  private showCombinedPopup(lngLat: mapboxgl.LngLat, layerFeatures: LayerFeatures[]): void {
    if (this.currentPopup) {
      this.currentPopup.remove();
    }

    const html = this.formatCombinedFeatures(layerFeatures);

    const mapHeight = this.map.getContainer().clientHeight;
    const maxHeight = Math.floor(mapHeight * 0.75);

    this.currentPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'wms-feature-popup',
      maxWidth: '400px'
    })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(this.map);

    const popupEl = this.currentPopup.getElement();
    if (popupEl) {
      popupEl.style.setProperty('--popup-max-height', `${maxHeight}px`);
    }

    this.currentPopup.on('close', () => {
      this.currentPopup = null;
    });

    requestAnimationFrame(() => {
      this.panToShowPopup(lngLat);
    });
  }

  /**
   * Pan the map to ensure the popup is fully visible
   */
  private panToShowPopup(lngLat: mapboxgl.LngLat): void {
    if (!this.currentPopup) return;

    const popupEl = this.currentPopup.getElement();
    if (!popupEl) return;

    const mapContainer = this.map.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();
    const popupRect = popupEl.getBoundingClientRect();

    const padding = 20;
    let panX = 0;
    let panY = 0;

    if (popupRect.left < mapRect.left + padding) {
      panX = popupRect.left - mapRect.left - padding;
    } else if (popupRect.right > mapRect.right - padding) {
      panX = popupRect.right - mapRect.right + padding;
    }

    if (popupRect.top < mapRect.top + padding) {
      panY = popupRect.top - mapRect.top - padding;
    } else if (popupRect.bottom > mapRect.bottom - padding) {
      panY = popupRect.bottom - mapRect.bottom + padding;
    }

    if (panX !== 0 || panY !== 0) {
      this.map.panBy([panX, panY], { duration: 300 });
    }
  }

  /**
   * Format features from multiple layers into combined HTML
   */
  private formatCombinedFeatures(layerFeatures: LayerFeatures[]): string {
    const sections = layerFeatures.map(({ layerName, features }) => {
      const featureHtml = features.map((feature, idx) => {
        if (feature._html) {
          return `<div class="wms-popup-html-content">${feature._html}</div>`;
        }

        const properties = feature.properties || feature;
        return this.formatFeatureProperties(properties, idx + 1, features.length > 1);
      }).join('');

      return `
        <div class="wms-popup-layer-section">
          <div class="wms-popup-layer-header">${layerName}</div>
          ${featureHtml}
        </div>
      `;
    }).join('<hr class="wms-popup-divider"/>');

    return `<div class="wms-popup-content">${sections}</div>`;
  }

  /**
   * Format a single feature's properties into HTML table
   */
  private formatFeatureProperties(
    properties: Record<string, any>,
    featureNum?: number,
    showNum?: boolean
  ): string {
    const displayProps = Object.entries(properties)
      .filter(([key, value]) => {
        if (value === null || value === undefined) return false;
        if (key.startsWith('_') || key === 'geometry') return false;
        return true;
      });

    if (displayProps.length === 0) {
      return `<p class="wms-popup-empty">No properties</p>`;
    }

    const header = showNum && featureNum ? `<div class="wms-popup-feature-num">Feature ${featureNum}</div>` : '';

    const rows = displayProps
      .map(([key, value]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return `<tr><td class="wms-popup-label">${label}</td><td class="wms-popup-value">${value}</td></tr>`;
      })
      .join('');

    return `
      ${header}
      <div class="wms-popup-table-wrapper">
        <table class="wms-popup-table">
          ${rows}
        </table>
      </div>
    `;
  }

  /**
   * Clean up the coordinator
   */
  public destroy(): void {
    this.disable();
  }
}
